import type { Gender } from '@/objects/generated';
import { getEventAge } from './dateutils';
import { useCommonStore, useEventCommonStore } from '@/stores';
import type { CategoryId, GroupId, RaceId } from './typealiases';

/**
 * Filters categories using the current event data
 *
 * To get age filtering {@link birthday} must be supplied.
 *
 * To get gender filtering {@link gender} must be supplied.
 *
 * @param input The input parameters
 * @returns The categories matching
 */
export const findAvailableCategoriesForGenderAndBirthdayInCurrentEvent = (input: {
    /**
     * Gender to filter to
     */
    gender?: Gender | null;

    /**
     * Birthday to filter to
     *
     * This will be calculated to an age relative to the event date, using the sport season start rules.
     * @note This will NOT work if you are not SuperAdmin since all the dates will be set to 01-01-yyyy for privacy reasons. @use {@link age} instead using EventAge on the Contestant property
     */
    birthday?: Date | null;

    /**
     * Pre-calculated age to filter to
     *
     * If this is supplied {@link birthday} will be ignored.
     */
    age?: number | null;

    /**
     * The supplied id will always be in the result.
     *
     * This can be used if we are finding the categories for a contestant, we always want to include the category the contestant already has
     */
    alwaysIncludeCategory?: number | null;

    /**
     * Filter to only classes that are available for a specific race
     */
    raceId?: RaceId;

    /**
     * Filter only to classes available for a specific group
     */
    groupId?: GroupId;
}): Array<CategoryId> => {
    const { age, birthday, gender, alwaysIncludeCategory, raceId, groupId } = input;

    const eventCommonStore = useEventCommonStore();
    const commonStore = useCommonStore();

    const eventDate = eventCommonStore.Event?.Date;
    const sport = commonStore.Sports.find((s) => s.ID === eventCommonStore.Event?.SportID);
    const categories = eventCommonStore.Categories;

    // in relay we do not filter on age or gender
    const isRelay = (raceId && eventCommonStore.Races.find((r) => r.ID === raceId)?.RelayTeamMinContestants) ?? 0 > 0;

    let matchingCategories = [...categories];

    // Filter to age
    if (!isRelay && (age || birthday) && eventDate && sport) {
        const eventAge = age ?? getEventAge(birthday!, eventDate, sport);
        if (eventAge) {
            matchingCategories = matchingCategories.filter((c) => (c.AgeFrom ?? 0) <= eventAge && (c.AgeTo ?? 9999) >= eventAge);
        }
    }
    if (!isRelay && age && !sport) {
        matchingCategories = matchingCategories.filter((c) => (c.AgeFrom ?? 0) <= age && (c.AgeTo ?? 9999) >= age);
    }

    // Filter to gender
    if (!isRelay && gender) {
        matchingCategories = matchingCategories.filter((c) => c.Gender === 'n' || c.Gender === gender);
    }

    // Filter to race
    if (raceId) {
        const connectionsforThisRace = eventCommonStore.Groups.filter((g) => g.RaceID === raceId).flatMap((g) => g.CategoryGroupConnection);
        // if there is no connections for this race we get everything
        if (connectionsforThisRace.length > 0) {
            const categoriesForRace = connectionsforThisRace.map((cg) => cg.CategoryID);
            matchingCategories = matchingCategories.filter((c) => categoriesForRace.includes(c.ID));
        }
    }

    // Filter to group
    if (groupId) {
        const categoriesForGroup = eventCommonStore.Groups.filter((g) => g.ID === groupId)
            .flatMap((g) => g.CategoryGroupConnection)
            .map((cg) => cg.CategoryID);
        matchingCategories = matchingCategories.filter((c) => categoriesForGroup.includes(c.ID));
    }

    // If we have a alwaysIncludeCategory we need to make sure it is in the result
    if (alwaysIncludeCategory) {
        if (!matchingCategories.find((c) => c.ID === alwaysIncludeCategory)) {
            const cat = categories.find((c) => c.ID === alwaysIncludeCategory);
            if (cat) {
                matchingCategories.push(cat);
            }
        }
    }

    return matchingCategories.map((c) => c.ID);
};

/**
 * Helpers to get the event age for the current event
 * @param birthday
 * @returns
 */
export const getEventAgeForCurrentEvent = (birthday: Date | null): number | null => {
    if (!birthday) {
        return null;
    }
    const eventCommonStore = useEventCommonStore();
    const commonStore = useCommonStore();
    const eventDate = eventCommonStore.Event?.Date;
    const sport = commonStore.Sports.find((s) => s.ID === eventCommonStore.Event?.SportID);
    if (eventDate && sport) {
        return getEventAge(birthday, eventDate, sport);
    }

    return null;
};
